import {
  Component, Inject, OnDestroy, OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogRef,
} from '@angular/material/dialog';
import { CatalogSuggestionsService } from 'src/app/services/catalog-suggestions.service';
import { EntityService } from 'src/app/services/entity.service';
import { formatDate as formatDateUtil } from 'src/app/utils/dates.utils';

@Component({
  selector: 'app-my-catalog-suggestion-details',
  templateUrl: './my-catalog-suggestion-details.component.html',
  styleUrls: [ './my-catalog-suggestion-details.component.scss' ],
})
export class MyCatalogSuggestionDetailsComponent implements OnInit, OnDestroy {
  course: any ={
    catalogSuggestionId: '',
    name: '',
    creditHours: '',
    courseType: '',
    instructor: '',
    cost: '',
    webUrl: '',
    description: '',
    async: false,
    virtual: false,
    anytime: false,
    location: '',
    roomNumber: '',
    courseDate: '',
    startTime: '',
    endTime: '',
  }
  isLoading: any;
  catalogSuggestionSubscription: any;
  showUploadDocumentsModal = false;
  autoOpenUpload = false;

  constructor(
    public dialogRef: MatDialogRef<MyCatalogSuggestionDetailsComponent>,
    public dialog: MatDialog, @Inject(MAT_DIALOG_DATA) public data: any,
    private catalogSuggestionsService: CatalogSuggestionsService,
    private entityService: EntityService,
  ) { }

  ngOnInit(): void {
    if (!this.entityService.getLocalTimezone()) {
      this.entityService.getEntity('Config');
    }
    this.loadCatalogSuggestion();
  }

  loadCatalogSuggestion(): void {
    this.isLoading = true;
    this.autoOpenUpload = this.data?.autoOpenUpload || false;
    this.catalogSuggestionSubscription = this.catalogSuggestionsService.getCatalogSuggestion(this.data.id).subscribe({
      next: (response: any) => {
        try {
          // The response structure: data is the course, data.CourseItems[0] is the course item
          const course = response?.data || {};
          const courseItem = course?.CourseItems?.[0] || {};
          
          const isAsync = courseItem?.preRecordedOption === 'ASYNC';
          const isAnytime = courseItem?.preRecordedOption === 'ANYTIME';
          
          this.course = {
            ...course,
            ...courseItem,
            name: course?.courseName || '',
            creditHours: course?.trackingValue || '',
            courseType: course?.CourseType?.courseType || course?.courseType || '',
            instructor: courseItem?.instructor || null,
            cost: course?.courseCost || null,
            webUrl: courseItem?.hyperlink || null,
            description: course?.courseDescription || '',
            async: isAsync,
            virtual: courseItem?.virtualFlag === 1,
            anytime: isAnytime,
            location: courseItem?.location || '',
            roomNumber: courseItem?.roomNumber || null,
            courseDate: this.formatCourseDate(isAsync, courseItem),
            startTime: this.formatStartTime(isAnytime, courseItem),
            endTime: this.formatEndTime(isAnytime, courseItem),
            // Ensure CourseItems is included
            CourseItems: course?.CourseItems || [],
            // Preserve IDs
            courseItemId: courseItem?.id || null,
            courseId: course?.id || courseItem?.courseId || null,
          }

                    // Auto-open upload documents modal if requested and courseItemId exists
          if (this.autoOpenUpload && this.getCourseItemId()) {
            setTimeout(() => {
              this.openUploadDocumentsModal();
            }, 100);
          }

        } catch (error) {
          console.error('Error processing catalog suggestion data:', error);
        } finally {
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        console.error('Error getting catalog suggestion', error);
        this.isLoading = false;
      },
    });
  }

  onDocumentsUploaded(): void {
    // Reload catalog suggestion data to refresh document list
    this.loadCatalogSuggestion();
    // Close the upload modal after successful upload
    this.closeUploadDocumentsModal();
    // Emit result to parent
    this.dialogRef.close('uploaded');
  }

  getCourseItemId(): number | null {
    // Priority: courseItemId > CourseItems[0].id > id (but only if it's not the courseId)
    if (this.course?.courseItemId) {
      return Number(this.course.courseItemId);
    }
    if (this.course?.CourseItems) {
      if (Array.isArray(this.course.CourseItems) && this.course.CourseItems.length > 0) {
        return Number(this.course.CourseItems[0].id);
      }
      if (typeof this.course.CourseItems === 'object') {
        return Number(this.course.CourseItems.id);
      }
    }
    // Only use id if it's not the same as courseId (to avoid using courseId instead of courseItemId)
    if (this.course?.id && this.course?.id !== this.course?.courseId) {
      return Number(this.course.id);
    }
    if (this.course?.course_item_id) {
      return Number(this.course.course_item_id);
    }
    return null;
  }

  openUploadDocumentsModal(): void {
    this.showUploadDocumentsModal = true;
  }

  closeUploadDocumentsModal(): void {
    this.showUploadDocumentsModal = false;
  }

  private formatCourseDate(isAsync: boolean, courseItem: any): string {
    if (isAsync) return 'Async';
    return courseItem?.dateOffered ? formatDateUtil.asDateLocal(courseItem.dateOffered) : '';
  }

  private formatStartTime(isAnytime: boolean, courseItem: any): string {
    if (isAnytime) return 'Anytime';
    const tz = this.entityService.getLocalTimezoneWithFallback();
    const parsed = formatDateUtil.parseSessionTimeAsUtc(courseItem?.sessionStartDate);
    return parsed ? formatDateUtil.asTimeWithTimezone(parsed, tz) : '';
  }

  private formatEndTime(isAnytime: boolean, courseItem: any): string {
    if (isAnytime) return 'Anytime';
    const tz = this.entityService.getLocalTimezoneWithFallback();
    const parsed = formatDateUtil.parseSessionTimeAsUtc(courseItem?.sessionEndDate);
    return parsed ? formatDateUtil.asTimeWithTimezone(parsed, tz) : '';
  }

  cancelSuggestion(): void {
    this.dialogRef.close('cancel-suggestion');
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.catalogSuggestionSubscription.unsubscribe();
  }

}
